/**
 * One-tap Add to outreach — the `/api/private/signal-enrollments` resource (mb-frontend#4638).
 *
 * Every shape here is the generated type from mb-server's zod contract; nothing is retyped
 * by hand. See lib/outreach/signalEnrollmentContract.ts for the vocabulary.
 */

import { REQUEST_METHOD } from '@/constants';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import { apiInstance } from '@/utils/axios';
import type {
  CreateEnrollmentRequest,
  EnrollmentPreviewResponse,
  EnrollmentResponse,
  SenderReadiness,
} from '@/types/generated/signalEnrollment';

export type {
  CreateEnrollmentRequest,
  EnrollmentPreviewResponse,
  EnrollmentResponse,
  EnrollmentRowSummary,
  SenderReadiness,
} from '@/types/generated/signalEnrollment';

/**
 * What the confirmation is about: a signal (Today, evaluated account) or an account row.
 * `onBehalfOf`: an admin viewing a rep's signals acts as that rep (their mailboxes, templates).
 */
export type EnrollmentTarget = (
  | { signalId: string }
  | {
    companyDomain: string;
    companyName?: string | null;
    person?: CreateEnrollmentRequest extends infer R ? (R extends { person?: infer P } ? P : never) : never;
    /** Account taps: the activity the row shows, so an unscored account gets that activity's templates (board 1c). */
    observedSignalType?: string | null;
  }
) & { onBehalfOf?: string | null };

export function enrollmentTargetKey(target: EnrollmentTarget): string {
  const subject = enrollmentSubjectKey(target);
  return target.onBehalfOf ? `${subject}:as:${target.onBehalfOf}` : subject;
}

function enrollmentSubjectKey(target: EnrollmentTarget): string {
  return 'signalId' in target
    ? `signal:${target.signalId}`
    : `account:${target.companyDomain.toLowerCase()}${target.person ? `:${target.person.email || target.person.linkedinUrl || target.person.firstName}` : ''}${target.observedSignalType ? `:${target.observedSignalType}` : ''}`;
}

/**
 * The preview names the same target Enroll will send, so a People row previews the person it
 * will enroll (query strings are flat, hence the `person*` fields).
 */
export function previewParams(target: EnrollmentTarget): Record<string, string> {
  const actingFor: Record<string, string> = target.onBehalfOf ? { onBehalfOf: target.onBehalfOf } : {};
  if ('signalId' in target) return { signalId: target.signalId, ...actingFor };
  const params: Record<string, string> = { companyDomain: target.companyDomain, ...actingFor };
  if (target.companyName) params.companyName = target.companyName;
  if (target.observedSignalType && !target.person) params.observedSignalType = target.observedSignalType;
  const { person } = target;
  if (person?.firstName) {
    params.personFirstName = person.firstName;
    if (person.lastName) params.personLastName = person.lastName;
    if (person.title) params.personTitle = person.title;
    if (person.email) params.personEmail = person.email;
    if (person.linkedinUrl) params.personLinkedinUrl = person.linkedinUrl;
  }
  return params;
}

/**
 * `templateId` is the template the confirm has selected (phase 2): the sequence and first-touch
 * estimate then describe that template instead of the phase-1 default.
 */
export async function getEnrollmentPreview(target: EnrollmentTarget, templateId: number | null = null): Promise<EnrollmentPreviewResponse> {
  const params = previewParams(target);
  if (templateId !== null) params.templateId = String(templateId);
  const response = await apiInstance({
    url: API_ENDPOINTS.signalEnrollmentPreview,
    method: REQUEST_METHOD.GET,
    params,
  });
  return response.data;
}

export async function getSenderReadiness(): Promise<SenderReadiness> {
  const response = await apiInstance({ url: API_ENDPOINTS.signalEnrollmentReadiness, method: REQUEST_METHOD.GET });
  return response.data;
}

/**
 * Which screen a tap came from, sent as a header so the server can report it on the events the
 * browser cannot produce — above all a refusal, where no intent is written and the browser's own
 * event is all that would otherwise say where the rep was standing. A header, not a body field,
 * because it is telemetry: it must not become part of the request contract the server validates.
 */
function surfaceHeaders(surface?: string | null): Record<string, string> | undefined {
  return surface ? { 'x-mb-surface': surface } : undefined;
}

/** Enroll. 201 with the new intent, or 200 with the one that already exists — the body is the same. */
export async function createEnrollment(body: CreateEnrollmentRequest, surface?: string | null): Promise<EnrollmentResponse> {
  const response = await apiInstance({
    url: API_ENDPOINTS.signalEnrollments,
    method: REQUEST_METHOD.POST,
    data: body,
    headers: surfaceHeaders(surface),
  });
  return response.data;
}

export async function getEnrollment(enrollmentId: string): Promise<EnrollmentResponse> {
  const response = await apiInstance({ url: API_ENDPOINTS.signalEnrollment(enrollmentId), method: REQUEST_METHOD.GET });
  return response.data;
}

export async function cancelEnrollment(enrollmentId: string, surface?: string | null): Promise<EnrollmentResponse> {
  const response = await apiInstance({
    url: API_ENDPOINTS.signalEnrollmentCancel(enrollmentId),
    method: REQUEST_METHOD.POST,
    headers: surfaceHeaders(surface),
  });
  return response.data;
}

/**
 * Retry a failed lookup as a fresh attempt on the same signal.
 *
 * @param query Free-text "who should we look for at this account" — the same single-prompt
 *   shape the audience Find contacts flow takes. The server turns it into search criteria in
 *   one LLM call and discovery searches those titles instead of the evaluator's, for this
 *   attempt and every later one until a new query replaces them. Omit it to re-run the
 *   identical search, which is right after a transient failure and useless after
 *   `no_contacts_found` — there the same search returns the same nobody.
 * @param providers Which data sources to search, in order, as audience-processor's own
 *   provider names (see lib/searchProviders.ts). Omit for the usual order, which tries more
 *   than one — so a single name here is narrower than the default, never broader.
 */
export async function retryEnrollment(
  enrollmentId: string,
  surface?: string | null,
  query?: string | null,
  providers?: readonly string[] | null,
): Promise<EnrollmentResponse> {
  const trimmed = query?.trim();
  const picked = providers && providers.length > 0 ? [...providers] : null;
  const body = {
    ...(trimmed ? { query: trimmed } : {}),
    ...(picked ? { providers: picked } : {}),
  };
  const response = await apiInstance({
    url: API_ENDPOINTS.signalEnrollmentRetry(enrollmentId),
    method: REQUEST_METHOD.POST,
    headers: surfaceHeaders(surface),
    ...(Object.keys(body).length > 0 ? { data: body } : {}),
  });
  return response.data;
}

/** @param templateId mb-frontend#4638 phase 2: an explicit pick from the roster; omitted lets the server auto-choose as before. */
export function toCreateRequest(
  target: EnrollmentTarget,
  templateId?: number,
  senderMailbox?: CreateEnrollmentRequest['senderMailbox'],
): CreateEnrollmentRequest {
  // "Your mailbox": sent only when the rep picked one; omitted keeps the default rotation.
  const mailbox = senderMailbox ? { senderMailbox: { id: senderMailbox.id, platform: senderMailbox.platform } } : {};
  const actingFor = target.onBehalfOf ? { onBehalfOf: target.onBehalfOf } : {};
  if ('signalId' in target) return { signalId: target.signalId, templateId, ...mailbox, ...actingFor };
  return {
    companyDomain: target.companyDomain,
    companyName: target.companyName ?? null,
    person: target.person ?? null,
    ...(target.observedSignalType && !target.person ? { observedSignalType: target.observedSignalType } : {}),
    templateId,
    ...mailbox,
    ...actingFor,
  };
}
